import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { DocumentLanguage, ReportMetadata, TextView } from './documents';
import type { FormType } from './forms';

/** One value of a chart's source data, in source order (the table under the chart shows them all). */
export interface ChartItem {
  label: TextView;
  /** Canonical decimal, e.g. "12.5". */
  value: string;
  /** Index of the slice that shows this value (an item may be folded into "Ostatné"); absent when not charted. */
  slice?: number | null;
}

/** One slice of the donut (at most six; the sixth may be the "Ostatné" bucket). */
export interface ChartSlice {
  label: TextView;
  value: string;
  other: boolean;
}

/** A donut chart of one field marked "Zobraziť v grafe" in the template (rendered from the report snapshot). */
export interface ReportChart {
  id: string;
  formType: FormType;
  /** The status form the values come from. */
  form: TextView;
  /** Chart title: the field label (and the table column for TABLE fields). */
  title: TextView;
  /** Sum of the charted (positive) values, canonical decimal. */
  total: string;
  slices: ChartSlice[];
  items: ChartItem[];
  /** e.g. values that cannot be shown in a donut (negative, not a number). */
  notes: TextView[];
}

export interface ReportCharts {
  report: ReportMetadata;
  /** Language of the chart texts (labels come from the snapshot in this language). */
  lang: string;
  charts: ReportChart[];
}

/** Donut charts of a finished inspection's numeric values, from the frozen report snapshot. */
export function useReportCharts(inspectionId: string | undefined, lang: DocumentLanguage) {
  return useQuery({
    queryKey: ['inspections', inspectionId, 'report', 'charts', lang],
    queryFn: ({ signal }) => api<ReportCharts>(`/inspections/${inspectionId}/report/charts`, { query: { lang }, signal }),
    enabled: !!inspectionId,
    placeholderData: (previous) => previous,
    staleTime: Infinity,
  });
}
